import type { Language, Transaction } from "./finance.ts";

export type BusinessUnit = {
  slug: string;
  name: string;
  shortName: string;
  color: string;
  active: boolean;
  displayOrder: number;
  system: boolean;
};

export type RecipientBrandMapping = {
  id?: string;
  recipientName: string;
  normalizedName: string;
  businessUnit: string;
  /** Candidate brands, not transaction ownership. */
  businessUnits?: string[];
  matchKind?: "recipient" | "bank";
  createdAt?: string;
  updatedAt?: string;
};

export type UnmappedRecipient = {
  normalizedName: string;
  recipientName: string;
  banks: string[];
  transactionCount: number;
  totalAmount: number;
  lastTransactionDate: string;
};

export const defaultBusinessUnits: BusinessUnit[] = [
  { slug: "eventkita", name: "EventKita", shortName: "EventKita", color: "#db0fb3", active: true, displayOrder: 10, system: true },
  { slug: "meaningful_ways", name: "Meaningful Ways", shortName: "MW", color: "#d946ef", active: true, displayOrder: 20, system: true },
  { slug: "tumbuh_bersama", name: "Titik Balik", shortName: "TB", color: "#8b5cf6", active: true, displayOrder: 30, system: true },
  { slug: "unassigned", name: "Belum ditentukan", shortName: "—", color: "#94a3b8", active: true, displayOrder: 999, system: true },
];

export function normalizeRecipientName(value: string | null | undefined) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function businessUnitName(slug: string, language: Language, units: BusinessUnit[] = defaultBusinessUnits): string {
  if (slug.includes("+")) return slug.split("+").map((part) => businessUnitName(part, language, units)).join(" + ");
  const unit = units.find((candidate) => candidate.slug === slug);
  if (unit) return unit.name;
  return slug === "unassigned" ? (language === "id" ? "Belum ditentukan" : "Unassigned") : slug;
}

export function activeBusinessUnits(units: BusinessUnit[]) {
  return units.filter((unit) => unit.active && unit.slug !== "unassigned").sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));
}

export function actualBrands(row: Pick<Transaction, "businessUnit" | "businessUnits">): string[] {
  return [...new Set((row.businessUnits ?? [row.businessUnit]).filter((value) => value && value !== "unassigned"))].sort();
}

export function brandContext(row: Pick<Transaction, "businessUnit" | "businessUnits">) {
  return actualBrands(row).join("+") || "unassigned";
}

export function isCollaboration(row: Pick<Transaction, "businessUnit" | "businessUnits">) {
  return actualBrands(row).length > 1;
}

export function matchesBrandFilter(row: Transaction, value: string) {
  return value === "all" || (value === "collaboration" ? isCollaboration(row)
    : value === "unassigned" ? !actualBrands(row).length
    : actualBrands(row).length === 1 && actualBrands(row)[0] === value);
}

/** Any selected filter matches; collaboration stays exclusive to its own filter. */
export function matchesBrandFilters(row: Transaction, filters: string[]) {
  return filters.length === 0 || filters.some((filter) => matchesBrandFilter(row, filter));
}

export function transactionBrandName(row: Pick<Transaction, "businessUnit" | "businessUnits">, language: Language, units?: BusinessUnit[]) {
  return actualBrands(row).map((value) => businessUnitName(value, language, units)).join(" + ")
    || businessUnitName("unassigned", language, units);
}

export function recipientBrandCandidates(row: Transaction, mappings: RecipientBrandMapping[]) {
  const name = normalizeRecipientName(row.beneficiaryName);
  const bank = normalizeRecipientName(row.beneficiaryBank);
  const nameMatches = mappings.filter((mapping) => (mapping.matchKind || "recipient") === "recipient" && name && mapping.normalizedName === name);
  const matches = nameMatches.length ? nameMatches : mappings.filter((mapping) => mapping.matchKind === "bank" && bank && mapping.normalizedName === bank);
  return [...new Set(matches.flatMap((mapping) => mapping.businessUnits || [mapping.businessUnit]).filter((value) => value && value !== "unassigned"))].sort();
}

export function applyRecipientBrandMappings(
  rows: Transaction[],
  mappings: RecipientBrandMapping[],
) {
  return rows.map((row) => {
    if (row.businessUnitMappingSource === "manual" || row.businessUnitMappingSource === "description") return row;
    const candidates = recipientBrandCandidates(row, mappings);
    if (!candidates.length && !normalizeRecipientName(row.beneficiaryName) && row.businessUnitMappingSource === "code") return { ...row, businessUnits: row.businessUnit === "unassigned" ? [] : [row.businessUnit], brandCandidates: [] };
    return { ...row, businessUnit: candidates.length === 1 ? candidates[0] : "unassigned", businessUnits: candidates.length === 1 ? candidates : [], brandCandidates: candidates, businessUnitMappingSource: candidates.length === 1 ? "recipient" as const : undefined };
  });
}

export function unmappedRecipients(
  rows: Transaction[],
  mappings: RecipientBrandMapping[],
): UnmappedRecipient[] {
  const mapped = new Set(mappings.filter((mapping) => (mapping.matchKind || "recipient") === "recipient").map((mapping) => mapping.normalizedName));
  const grouped = new Map<string, UnmappedRecipient>();
  for (const row of rows) {
    const normalizedName = normalizeRecipientName(row.beneficiaryName);
    if (!normalizedName || mapped.has(normalizedName)) continue;
    const existing = grouped.get(normalizedName) || {
      normalizedName,
      recipientName: row.beneficiaryName?.trim() || normalizedName,
      banks: [],
      transactionCount: 0,
      totalAmount: 0,
      lastTransactionDate: "",
    };
    existing.transactionCount += 1;
    existing.totalAmount += row.amount;
    if (row.beneficiaryBank && !existing.banks.includes(row.beneficiaryBank)) existing.banks.push(row.beneficiaryBank);
    if (row.date > existing.lastTransactionDate) existing.lastTransactionDate = row.date;
    grouped.set(normalizedName, existing);
  }
  return [...grouped.values()].sort((a, b) => b.transactionCount - a.transactionCount || a.recipientName.localeCompare(b.recipientName));
}
